import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Layout from './components/Layout';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import Dashboard from './pages/Dashboard';
import PricingHeaders from './pages/PricingHeaders';
import PlansPage from './pages/PlansPage';
import PriceLists from './pages/PriceLists';
import Contracts from './pages/Contracts';
import Customers from './pages/Customers';
import PlanDetails from './pages/PlanDetails';
import ContractDetails from './pages/ContractDetails';
import CustomerDetails from './pages/CustomerDetails';
import ContractAssignments from './pages/ContractAssignments';
import UsersManagement from './pages/UsersManagement';
import AuditTrail from './pages/AuditTrail';
import SecurityDashboard from './pages/SecurityDashboard';
import Backups from './pages/Backups';
import DataImport from './pages/DataImport';
import DataImportImproved from './pages/DataImportImproved';
import ServerManagement from './pages/ServerManagement/ServerManagement';
import ServerDetails from './pages/ServerManagement/ServerDetails';
import CrudDashboard from './pages/CrudDashboard';
import Invoices from './pages/Invoices';
import InvoiceDetails from './pages/InvoiceDetails';
import ModernInvoiceDetails from './pages/ModernInvoiceDetails';
import Companies from './pages/Companies';
import PaymentProvidersPage from './pages/PaymentProviders';
import Templates from './pages/Templates';
import Features from './pages/Features';
import ImportChangesDashboard from './pages/ImportChangesDashboard';
import { ReviewAutomationPage } from './pages/ReviewAutomationPage';
import BIDashboard from './pages/BIDashboard';
import { StripeB2BCheckoutPage } from './pages/StripeB2BCheckoutPage';
import { PaymentSuccessPage } from './pages/PaymentSuccessPage';
import { PaymentRequiredPage } from './pages/PaymentRequiredPage';
import { useCSRF } from './hooks/useCSRF';
import { authService } from './services/authService';

// Create an auth context to share auth state
export const AuthContext = React.createContext<{
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
}>({
  isAuthenticated: false,
  isLoading: true,
  checkAuth: async () => {}
});

function AppContent() {
  const location = useLocation();
  const [authState, setAuthState] = useState<{
    isAuthenticated: boolean;
    isChecking: boolean;
    hasChecked: boolean;
  }>({
    isAuthenticated: false,
    isChecking: true,
    hasChecked: false
  });

  useEffect(() => {
    // Only check auth once on mount
    if (!authState.hasChecked) {
      const checkAuth = async () => {
        try {
          // Check if we have a token first
          const hasToken = !!authService.getToken();
          if (!hasToken) {
            // No token, definitely not authenticated
            setAuthState({
              isAuthenticated: false,
              isChecking: false,
              hasChecked: true
            });
            return;
          }

          // We have a token, verify it's valid
          const authenticated = await authService.initializeAuth();
          setAuthState({
            isAuthenticated: authenticated,
            isChecking: false,
            hasChecked: true
          });
        } catch (error) {
          // Avoid logging sensitive auth errors to the console
          // Don't log aborted requests
          if ((error as any)?.code !== 'ECONNABORTED' && (error as any)?.code !== 'ERR_CANCELED') {
            console.error('Auth check failed:', error);
          }
          setAuthState({
            isAuthenticated: false,
            isChecking: false,
            hasChecked: true
          });
        }
      };

      checkAuth();
    }
  }, [authState.hasChecked]);

  const handleLoginSuccess = useCallback(() => {
    // Update auth state after successful login
    setAuthState({
      isAuthenticated: true,
      isChecking: false,
      hasChecked: true
    });
  }, []);

  // Redirect to login if unauthenticated after check
  const isLoginPage = location.pathname === '/login';
  if (!authState.isChecking && !authState.isAuthenticated && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading only if we're checking auth AND not on the login page
  const showLoading = authState.isChecking && !isLoginPage;

  if (showLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={<Login onLoginSuccess={handleLoginSuccess} />} 
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <PrivateRoute isAuthenticated={authState.isAuthenticated}>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/plans" element={<PlansPage />} />
                <Route path="/plans/:id" element={<PlanDetails />} />
                <Route path="/checkout/:planId" element={<StripeB2BCheckoutPage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                
                {/* Admin panel routes - only require authentication */}
                <Route path="/bi" element={<BIDashboard />} />
                <Route path="/crud-dashboard" element={<CrudDashboard />} />
                <Route path="/pricing-headers" element={<PricingHeaders />} />
                <Route path="/pricelists" element={<PriceLists />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/contracts/:id" element={<ContractDetails />} />
                <Route path="/contract-assignments" element={<ContractAssignments />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:id" element={<CustomerDetails />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/:id" element={<InvoiceDetails />} />
                <Route path="/invoices/:id/modern" element={<ModernInvoiceDetails />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/features" element={<Features />} />
                <Route path="/users" element={<UsersManagement />} />
                <Route path="/audit" element={<AuditTrail />} />
                <Route path="/backups" element={<Backups />} />
                <Route path="/security" element={<SecurityDashboard />} />
                <Route path="/data-import" element={<DataImport />} />
                <Route path="/data-import-improved" element={<DataImportImproved />} />
                <Route path="/import-changes" element={<ImportChangesDashboard />} />
                <Route path="/review-automation" element={<ReviewAutomationPage />} />
                <Route path="/servers" element={<ServerManagement />} />
                <Route path="/servers/:id" element={<ServerDetails />} />
                <Route path="/payment-providers" element={<PaymentProvidersPage />} />
                <Route path="/payment-required" element={<PaymentRequiredPage />} />
                <Route path="/unauthorized" element={<div className="p-8 text-center">You are not authorized to access this page.</div>} />
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // Initialize CSRF protection
  const { isTokenReady } = useCSRF();

  // Show loading state while CSRF token is being initialized
  if (!isTokenReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Initializing security...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;
